import { GRID_SIZE, DEFAULT_GRID_COLS, GRID_ROWS as DEFAULT_GRID_ROWS } from './seatUtils';

const TEAL = '#0F766E';
const CORAL = '#E11D48';
const VIP = '#BE185D';
const STAGE = '#4B5563';

const uid = (prefix) =>
  `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

/** Canvas y grows downward; angle 0 = right, π/2 = up (toward top of screen). */
const polar = (cx, cy, r, angle) => ({
  x: cx + r * Math.cos(angle),
  y: cy - r * Math.sin(angle),
});

/**
 * Annular sector (trapezoid-ish wedge) as polygon points.
 * Angles go from aStart → aEnd (typically left→right through the top).
 */
export function sectorPolygon(cx, cy, rInner, rOuter, aStart, aEnd, steps = 10) {
  const pts = [];
  for (let i = 0; i <= steps; i += 1) {
    const t = i / steps;
    const a = aStart + (aEnd - aStart) * t;
    pts.push(polar(cx, cy, rOuter, a));
  }
  for (let i = steps; i >= 0; i -= 1) {
    const t = i / steps;
    const a = aStart + (aEnd - aStart) * t;
    pts.push(polar(cx, cy, rInner, a));
  }
  return pts;
}

const boundsFromPoints = (points) => {
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  return {
    x: minX,
    y: minY,
    width: Math.max(1, maxX - minX),
    height: Math.max(1, maxY - minY),
  };
};

const makePolygonZone = ({
  name,
  color,
  points,
  capacity = '',
  sortOrder = 0,
  description = '',
  meta = {},
}) => {
  const box = boundsFromPoints(points);
  return {
    id: uid('zone'),
    name,
    description,
    color,
    capacity,
    sortOrder,
    isActive: true,
    type: 'POLYGON',
    x: box.x,
    y: box.y,
    width: box.width,
    height: box.height,
    polygonPoints: points,
    points,
    seats: [],
    ...meta,
  };
};

const makeRectZone = ({
  name,
  color,
  x,
  y,
  width,
  height,
  capacity = '',
  sortOrder = 0,
  description = '',
  meta = {},
}) => ({
  id: uid('zone'),
  name,
  description,
  color,
  capacity,
  sortOrder,
  isActive: true,
  type: 'RECTANGLE',
  x,
  y,
  width,
  height,
  seats: [],
  ...meta,
});

/**
 * Classic amphitheater / arena block map (biletinial-style):
 * Rings A / B / C as radial wedges + VIP + SAHNE marker zone.
 */
export function generateAmphitheaterZones(options = {}) {
  const {
    gridCols = DEFAULT_GRID_COLS,
    gridRows = DEFAULT_GRID_ROWS,
    rings = [
      { letter: 'A', count: 6 },
      { letter: 'B', count: 8 },
      { letter: 'C', count: 10 },
    ],
    includeVip = true,
    includeStage = true,
    /** Total arc in radians (π ≈ semicircle). */
    arcSpan = Math.PI * 0.92,
    /** Gap between adjacent blocks (radians). */
    gap = 0.012,
  } = options;

  const canvasW = gridCols * GRID_SIZE;
  const canvasH = gridRows * GRID_SIZE;
  const cx = canvasW * 0.5;
  // Stage sits near bottom; arc center slightly above stage
  const cy = canvasH * 0.78;

  const maxR = Math.min(canvasW * 0.46, canvasH * 0.62);
  const stageClear = maxR * 0.18;
  const ringCount = rings.length;
  const ringThickness = (maxR - stageClear) / ringCount;

  const midAngle = Math.PI / 2; // top
  const aLeft = midAngle + arcSpan / 2;
  const aRight = midAngle - arcSpan / 2;

  const zones = [];
  let sort = 0;

  rings.forEach((ring, ringIndex) => {
    const rInner = stageClear + ringIndex * ringThickness + 4;
    const rOuter = stageClear + (ringIndex + 1) * ringThickness - 2;
    const n = Math.max(1, Number(ring.count) || 1);
    const slice = (aLeft - aRight) / n;

    for (let i = 0; i < n; i += 1) {
      const a0 = aLeft - i * slice - gap / 2;
      const a1 = aLeft - (i + 1) * slice + gap / 2;
      if (a0 <= a1) continue;
      const points = sectorPolygon(cx, cy, rInner, rOuter, a0, a1, 12);
      const alt = (ringIndex + i) % 2 === 0;
      zones.push(
        makePolygonZone({
          name: `${ring.letter}${i + 1}`,
          color: alt ? TEAL : CORAL,
          points,
          capacity: '',
          sortOrder: sort,
          description: `Blok ${ring.letter}${i + 1}`,
          meta: { ring: ring.letter, blockIndex: i + 1, template: 'amphitheater' },
        }),
      );
      sort += 1;
    }
  });

  if (includeVip) {
    const vipInner = stageClear * 0.35;
    const vipOuter = stageClear * 0.95;
    const vipSpan = 0.35;
    const points = sectorPolygon(
      cx,
      cy,
      vipInner,
      vipOuter,
      midAngle + vipSpan / 2,
      midAngle - vipSpan / 2,
      8,
    );
    zones.push(
      makePolygonZone({
        name: 'VIP Sandalye',
        color: VIP,
        points,
        sortOrder: sort,
        description: 'VIP ön sahne',
        meta: { ring: 'VIP', template: 'amphitheater' },
      }),
    );
    sort += 1;
  }

  if (includeStage) {
    const stageW = maxR * 0.9;
    const stageH = stageClear * 0.85;
    zones.push(
      makeRectZone({
        name: 'SAHNE',
        color: STAGE,
        x: cx - stageW / 2,
        y: cy + stageClear * 0.15,
        width: stageW,
        height: stageH,
        sortOrder: sort,
        description: 'Sahne alanı',
        meta: { isStage: true, template: 'amphitheater', selectable: false },
      }),
    );
  }

  return zones;
}

/** Flat rectangular area blocks (festival / standing sections). */
export function generateRectAreaBlocks(options = {}) {
  const {
    gridCols = DEFAULT_GRID_COLS,
    gridRows = DEFAULT_GRID_ROWS,
    rows = 3,
    cols = 4,
    labels = null,
  } = options;

  const canvasW = gridCols * GRID_SIZE;
  const canvasH = gridRows * GRID_SIZE;
  const padX = canvasW * 0.08;
  const padY = canvasH * 0.12;
  const gapX = 24;
  const gapY = 28;
  const usableW = canvasW - padX * 2 - gapX * (cols - 1);
  const usableH = canvasH - padY * 2 - gapY * (rows - 1) - canvasH * 0.12;
  const cellW = usableW / cols;
  const cellH = usableH / rows;
  const stageY = padY + rows * (cellH + gapY) + 16;
  const stageH = Math.max(48, canvasH - stageY - padY);

  const letter = (r) => String.fromCharCode(65 + r);
  const zones = [];
  let sort = 0;

  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      const name = labels?.[sort] || `${letter(r)}${c + 1}`;
      const alt = (r + c) % 2 === 0;
      zones.push(
        makeRectZone({
          name,
          color: alt ? TEAL : CORAL,
          x: padX + c * (cellW + gapX),
          y: padY + r * (cellH + gapY),
          width: cellW,
          height: cellH,
          sortOrder: sort,
          description: `Alan ${name}`,
          meta: { template: 'rect-blocks' },
        }),
      );
      sort += 1;
    }
  }

  zones.push(
    makeRectZone({
      name: 'SAHNE',
      color: STAGE,
      x: padX,
      y: stageY,
      width: canvasW - padX * 2,
      height: stageH,
      sortOrder: sort,
      description: 'Sahne',
      meta: { isStage: true, template: 'rect-blocks' },
    }),
  );

  return zones;
}

/** U-shaped tribune blocks around a center pitch / stage. */
export function generateUShapeZones(options = {}) {
  const { gridCols = DEFAULT_GRID_COLS, gridRows = DEFAULT_GRID_ROWS } = options;
  const canvasW = gridCols * GRID_SIZE;
  const canvasH = gridRows * GRID_SIZE;
  const margin = 80;
  const band = Math.min(220, canvasW * 0.14);
  const zones = [];
  let sort = 0;

  const blocks = [
    { name: 'Kuzey', x: margin + band, y: margin, w: canvasW - 2 * margin - 2 * band, h: band },
    { name: 'Kuzeybatı', x: margin, y: margin, w: band, h: band },
    { name: 'Kuzeydoğu', x: canvasW - margin - band, y: margin, w: band, h: band },
    { name: 'Batı', x: margin, y: margin + band, w: band, h: canvasH - 2 * margin - 2 * band },
    {
      name: 'Doğu',
      x: canvasW - margin - band,
      y: margin + band,
      w: band,
      h: canvasH - 2 * margin - 2 * band,
    },
    { name: 'Güneybatı', x: margin, y: canvasH - margin - band, w: band, h: band },
    { name: 'Güneydoğu', x: canvasW - margin - band, y: canvasH - margin - band, w: band, h: band },
  ];

  blocks.forEach((b, i) => {
    zones.push(
      makeRectZone({
        name: b.name,
        color: i % 2 === 0 ? TEAL : CORAL,
        x: b.x,
        y: b.y,
        width: b.w,
        height: b.h,
        sortOrder: sort++,
        meta: { template: 'u-shape' },
      }),
    );
  });

  zones.push(
    makeRectZone({
      name: 'SAHNE / SAHA',
      color: STAGE,
      x: margin + band + 40,
      y: margin + band + 40,
      width: canvasW - 2 * (margin + band + 40),
      height: canvasH - 2 * (margin + band + 40),
      sortOrder: sort,
      meta: { isStage: true, template: 'u-shape' },
    }),
  );

  return zones;
}

/** Catalog shown in wizard + editor. */
export const LAYOUT_TEMPLATES = [
  {
    id: 'blank',
    name: 'Boş canvas',
    description: 'Sıfırdan zone çiz (serbest / kutu / daire)',
    category: 'all',
    preview: 'blank',
    build: () => [],
  },
  {
    id: 'amphitheater',
    name: 'Amfitiyatro / Arena',
    description: 'A1–A6 · B1–B8 · C1–C10 + VIP + Sahne (bilet sitesi tarzı)',
    category: 'area',
    preview: 'amphitheater',
    build: (opts) => generateAmphitheaterZones(opts),
  },
  {
    id: 'amphitheater-compact',
    name: 'Kompakt amfi',
    description: 'A4 · B6 · C8 + VIP — daha az blok',
    category: 'area',
    preview: 'amphitheater',
    build: (opts) =>
      generateAmphitheaterZones({
        ...opts,
        rings: [
          { letter: 'A', count: 4 },
          { letter: 'B', count: 6 },
          { letter: 'C', count: 8 },
        ],
      }),
  },
  {
    id: 'rect-blocks',
    name: 'Dikdörtgen bloklar',
    description: '3×4 alan ızgarası + sahne — festival / ayakta',
    category: 'area',
    preview: 'rect',
    build: (opts) => generateRectAreaBlocks(opts),
  },
  {
    id: 'u-shape',
    name: 'U tribün',
    description: 'Kuzey / batı / doğu tribün blokları + saha',
    category: 'area',
    preview: 'u',
    build: (opts) => generateUShapeZones(opts),
  },
];

export function getLayoutTemplate(id) {
  return LAYOUT_TEMPLATES.find((t) => t.id === id) || LAYOUT_TEMPLATES[0];
}

export function buildTemplateZones(templateId, options = {}) {
  const tpl = getLayoutTemplate(templateId);
  return tpl.build(options) || [];
}
